import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * A dropdown that renders into <body> and pins itself under its anchor with position:fixed.
 *
 * Every menu in the mail toolbar used to be an absolutely-positioned child of `.menu-type-scroll`,
 * which `@media (max-width:768px)` turns into `overflow-x: auto`. Per CSS, `overflow-y: visible`
 * computes to `auto` as soon as the other axis is not `visible`, so the toolbar became a clipping
 * box on BOTH axes and every popup underneath it was sliced down to the toolbar's 55px height —
 * that is the "Move to Folder sheet shows only its title" report. A fixed-position portal sits
 * outside that box entirely, so it can never be clipped by an ancestor's overflow or lose a
 * z-index fight with a sibling stacking context.
 *
 * Same pattern the header's app-launcher already uses (see Header.js).
 */
const AnchoredMenu = ({
  open,
  anchorRef,
  onClose,
  align = 'left',
  width = 220,
  className = '',
  children,
}) => {
  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);

  // Position under the anchor, and keep it there while the page moves.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = anchorRef?.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const maxLeft = Math.max(8, window.innerWidth - width - 8);
      const rawLeft = align === 'right' ? r.right - width : r.left;
      setPos({ top: r.bottom + 6, left: Math.min(Math.max(8, rawLeft), maxLeft) });
    };
    place();
    window.addEventListener('resize', place);
    // capture: catches scrolling of any ancestor, not just the window.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, align, width]);

  // Dismiss on outside click or Escape. The anchor is excluded so its own click can toggle.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={`anchored-menu ${className}`.trim()}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width }}
      role="menu"
    >
      {children}
    </div>,
    document.body
  );
};

export default AnchoredMenu;
